import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { addToCart } from '../../../store/cartSlice';
import { removeFromWishlist } from '../../../store/wishlistSlice';
import { showShopToast } from '../../../utils/shopToast';
import './WishlistPage.css';
import ShopNav from '../../../components/shopNav/ShopNav';
import Footer from '../../../components/footer/footer';
import NavCategorias from '../../../components/navbar/NavCategorias';
import ServiceHighlights from '../../../components/servicehighlights/ServiceHighlights';
import ProductImageGallery from '../../../components/shop/ProductImageGallery';

// Función para formatear el precio en COP
const formatCOP = (value) => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

const WishlistPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const wishlistItems = useSelector(state => state.wishlist.items);
    const previousRoute = useSelector(state => state.navigation.previousRoute);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    const handleAddToCart = (product) => {
        dispatch(addToCart(product));
        showShopToast(`${product.name} agregado al carrito`, 'success');
    };

    const handleRemove = (id) => {
        dispatch(removeFromWishlist(id));
        showShopToast('Producto eliminado de la lista de deseos', 'warning');
    };

    return (
        <div className="wishlist-page">
            <ShopNav />
            <NavCategorias />

            <main className="wishlist-page-main">
                <section className="wishlist-page-hero wishlist-page-elevated-wrap wishlist-page-elevated-wrap--hero">
                    <div className="container wishlist-page-hero-inner">
                        <div className="wishlist-page-hero-copy">
                            <h1 className="display-5 animate__animated animate__fadeIn">Lista de deseos</h1>
                            <p className="lead animate__animated animate__fadeIn">
                                Guarda tus productos favoritos y compáralos antes de llevarlos al carrito.
                            </p>
                        </div>
                        <div className="wishlist-page-hero-logo-wrap animate__animated animate__fadeIn" aria-hidden="true">
                            <img src="/images/VPA.png" alt="Vintage Pro Audio" className="wishlist-page-hero-logo" />
                        </div>
                    </div>
                </section>

                <section className="wishlist-page-elevated-wrap">
                    <ServiceHighlights />
                </section>

                <section className="container wishlist-page-results">
                    <div className="wishlist-page-results-head">
                        <h2 className="wishlist-page-results-title">Tus productos guardados</h2>
                        <p className="wishlist-page-results-meta">{wishlistItems.length} producto(s) en tu wishlist</p>
                    </div>

                    {previousRoute && (
                        <div className="text-center mb-3">
                            <Link to={previousRoute} className="btn btn-outline-light wishlist-back-btn">
                                ← Volver a la sección anterior
                            </Link>
                        </div>
                    )}

                    {wishlistItems.length === 0 ? (
                        <div className="alert alert-info text-center wishlist-empty-state">Tu lista está vacía.</div>
                    ) : (
                        <div className="row g-4 wishlist-products-grid">
                            {wishlistItems.map(product => {
                                return (
                                    <div className="col-12 col-md-6 col-lg-4 wishlist-product-col" key={product.id}>
                                        <div
                                            className="card h-100 wishlist-product-card"
                                            onClick={() => navigate(`/producto/${product.id}`)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="card-body d-flex flex-column">
                                                <div className="wishlist-image-gallery-wrapper wishlist-card-media">
                                                  <button
                                                    type="button"
                                                    className="wishlist-card-remove"
                                                    onClick={(event) => {
                                                      event.stopPropagation();
                                                      handleRemove(product.id);
                                                    }}
                                                    aria-label={`Eliminar ${product.name} de la lista de deseos`}
                                                    title="Eliminar de la lista"
                                                  >
                                                    <i className="bi bi-trash" aria-hidden="true"></i>
                                                  </button>
                                                  <ProductImageGallery
                                                    imageUrl={product.imageUrl}
                                                    productName={product.name}
                                                    showPlayButton={false}
                                                    showFullscreenButton={false}
                                                    showThumbnails={false}
                                                    autoPlay={true}
                                                    slideInterval={4000}
                                                  />
                                        </div>

                                                <div className="wishlist-card-meta">
                                                    <span className="wishlist-card-chip">Wishlist</span>
                                                    <span className="wishlist-card-sku">Ref. {product.id}</span>
                                                </div>

                                            <h5 className="card-title">{product.name}</h5>
                                            <p className="card-text">{product.description}</p>

                                                <div className="wishlist-card-pricing">
                                                    <p className="fw-bold text-success">{formatCOP(product.priceCOP)}</p>
                                                </div>

                                            <div className="wishlist-card-actions mt-auto d-flex gap-2">
                                                <button
                                                    className="btn btn-success w-100"
                                                    onClick={(event) => {
                                                      event.stopPropagation();
                                                      handleAddToCart(product);
                                                    }}
                                                >
                                                    Agregar al carrito
                                                </button>
                                            </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>
            </main>

            {/* Toast */}
            <div
                className="toast-container position-fixed bottom-0 end-0 p-3 shop-toast-container"
                style={{ zIndex: 9999 }}
            >
                <div
                    className={`toast align-items-center text-bg-success border-0 ${showToast ? 'show' : 'hide'}`}
                    role="alert"
                    aria-live="assertive"
                    aria-atomic="true"
                >
                    <div className="d-flex">
                        <div className="toast-body">
                            {toastMessage}
                        </div>
                        <button
                            type="button"
                            className="btn-close btn-close-white me-2 m-auto"
                            onClick={() => setShowToast(false)}
                        ></button>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default WishlistPage;
